import { useRef, type CSSProperties, type ReactNode } from 'react';
import { Slot } from 'radix-ui';
import { cn } from '@worldcoo/ui/lib/utils';
import { useInView } from './use-in-view';

/**
 * The redesign's arrival animation, in two parts: a GROUP that goes off when it scrolls into view, and
 * the BOXES inside it that open one after another, left to right. All of the geometry and the timing
 * live in the `new-expand` block of `-styles/theme.css`; these two only wire a section up to it.
 *
 *     <ExpandGroup asChild>
 *       <section className="grid ...">
 *         <Expand index={0} asChild>
 *           <div className="rounded-3xl ...">…</div>
 *         </Expand>
 *
 * Both take `asChild`, so an existing box becomes an arriving box without gaining a wrapper element.
 * That matters more than it looks: every one of these boxes is a grid or flex child positioned by its
 * parent, and a `<div>` slipped in between would take the child's place in that layout and hand the box
 * a new, unstyled parent. `asChild` merges onto the element that is already there instead.
 */
/**
 * The stagger every hero uses. Not the home bento, which sits behind a `Suspense` boundary and
 * already arrives into a settled document. A bigger step lengthens `lead` too — they share a clock.
 */
export const EXPAND_STEP_ROOMY = 180;

export function ExpandGroup({
	lead = 0,
	step,
	asChild = false,
	className,
	children,
}: {
	/**
	 * Beats to wait before the first box opens, in the same steps `Expand`'s `index` counts.
	 *
	 * The home page needs none and is the reason this exists. Its bento sits inside a `Suspense`
	 * boundary, so React hydrates it after the rest of the document and the sweep starts a beat or two
	 * late on its own — which turned out to read better than the other pages, where a hero hydrates
	 * immediately and its boxes open into a document that is still arriving. This is that accident,
	 * made deliberate and available to a group that has no boundary to inherit it from.
	 */
	lead?: number;
	/** Milliseconds between two boxes opening; overrides the stylesheet default. */
	step?: number;
	/** Merge onto the child element rather than rendering a `<div>` of our own. */
	asChild?: boolean;
	className?: string;
	children: ReactNode;
}) {
	// Typed to the element this renders WITHOUT `asChild`, so one ref satisfies both branches; under
	// `asChild` it holds whatever the child turns out to be, which the observer does not care about.
	const ref = useRef<HTMLDivElement>(null);
	// A little inside the viewport, so a group is not spent while it is still a sliver at the bottom
	// edge. A group above the fold is already past that on load and simply arrives.
	//
	// `useInView` answers `false` on the server and on the first client render alike, so hydration sees
	// one answer and the group starts un-arrived wherever it is first painted. It only ever goes one
	// way, because this is an ENTRANCE: someone who scrolls back up to a hero has already watched it.
	const arrived = useInView(ref, { margin: '-64px' });
	const Comp = asChild ? Slot.Root : 'div';

	// One trigger per GROUP rather than one per box, deliberately: the sweep's whole point is that the
	// boxes arrive in relation to each other, and observing each of them separately makes the order a
	// function of the reader's scroll speed instead of the design.
	return (
		<Comp
			ref={ref}
			data-revealed={arrived || undefined}
			className={className}
			// Both inherit down to every box, so one number moves the whole sweep.
			style={
				lead || step
					? ({
							...(lead ? { '--expand-lead': lead } : null),
							...(step ? { '--expand-step': `${step}ms` } : null),
						} as CSSProperties)
					: undefined
			}
		>
			{children}
		</Comp>
	);
}

export function Expand({
	index = 0,
	radius,
	asChild = false,
	className,
	children,
}: {
	/**
	 * Where this box is in the sweep, in steps. Whole numbers walk it left to right — one per COLUMN —
	 * and fractions are how a stacked pair reads as a pair: `1` and `1.5` in the same column trail each
	 * other by half of what a neighbouring column does.
	 */
	index?: number;
	/**
	 * The box's own corner radius, when it is not the `rounded-3xl` the sweep assumes. The opening bar
	 * is a rounded CLIP, and a clip that does not match the box it is clipping is visible as the corners
	 * changing shape while the box grows.
	 */
	radius?: string;
	/** Merge onto the child element rather than rendering a `<div>` of our own. */
	asChild?: boolean;
	className?: string;
	children: ReactNode;
}) {
	const Comp = asChild ? Slot.Root : 'div';

	return (
		<Comp
			className={cn('new-expand', className)}
			style={{ '--expand-i': index, ...(radius ? { '--expand-radius': radius } : null) } as CSSProperties}
		>
			{children}
		</Comp>
	);
}
